import React, { useCallback, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import "../App.css";
import useFocusTrap from "../hooks/useFocusTrap";

const LOGO_ASPECTS = {
  wide: {
    label: "5:1 Wide",
    aspect: 5 / 1,
    outW: 2000,
    outH: 400,
    previewHeight: 64,
    hint: "Best for wide wordmarks",
  },
  standard: {
    label: "4:3 Standard",
    aspect: 4 / 3,
    outW: 1600,
    outH: 1200,
    previewHeight: 120,
    hint: "Better for stacked logos",
  },
};

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

async function getCroppedLogo(imageSrc, cropPixels, outputWidth = 2000, outputHeight = 400) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = outputWidth;
  canvas.height = outputHeight;
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("Canvas context unavailable");
  }

  // Draw the selected crop into a wide 5:1 canvas so logos size consistently in email clients.
  ctx.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    outputWidth,
    outputHeight
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Canvas is empty"));
          return;
        }
        const file = new File([blob], `logo-${Date.now()}.png`, { type: "image/png" });
        const previewUrl = URL.createObjectURL(blob);
        resolve({ file, previewUrl });
      },
      "image/png",
      0.92
    );
  });
}

export default function LogoCropperModal({ src, onApply, onClose }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aspectKey, setAspectKey] = useState("wide");
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);

  useFocusTrap({
    isOpen: true,
    containerRef: modalRef,
    onClose,
    initialFocusRef: closeButtonRef,
    lockScroll: true,
  });

  const aspectConfig = LOGO_ASPECTS[aspectKey] || LOGO_ASPECTS.wide;

  const onCropComplete = useCallback((_croppedArea, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleAspectChange = (nextKey) => {
    if (!LOGO_ASPECTS[nextKey] || nextKey === aspectKey) return;
    setAspectKey(nextKey);
    // Reset the crop box when aspect ratio changes to avoid awkward framing.
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const handleApply = async () => {
    if (!croppedAreaPixels) return;
    setSaving(true);
    try {
      const result = await getCroppedLogo(src, croppedAreaPixels, aspectConfig.outW, aspectConfig.outH);
      onApply(result);
    } catch (_err) {
      // ignore crop failures
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 260 }}>
      <div
        ref={modalRef}
        className="modal cropping-modal logo-cropping-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="logo-cropper-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 id="logo-cropper-modal-title">Crop Logo</h3>
          <button ref={closeButtonRef} className="icon-btn" type="button" onClick={onClose} aria-label="Close logo editor">
            x
          </button>
        </div>
        <div className="logo-cropper-layout">
          <div className="cropper-container logo-cropper-container">
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              aspect={aspectConfig.aspect}
              cropShape="rect"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
          <aside className="logo-preview-panel" aria-hidden="true">
            <div className="logo-aspect-toggle" role="group" aria-label="Logo aspect ratio">
              {Object.entries(LOGO_ASPECTS).map(([key, cfg]) => (
                <button
                  key={key}
                  type="button"
                  className={`ghost small ${aspectKey === key ? "active" : ""}`}
                  onClick={() => handleAspectChange(key)}
                >
                  {cfg.label}
                </button>
              ))}
            </div>
            <p className="muted small" style={{ margin: 0 }}>
              {aspectConfig.hint}
            </p>
            <div className="logo-preview-box" style={{ height: aspectConfig.previewHeight }}>
              <img src={src} alt="" />
            </div>
            <p className="muted small" style={{ margin: 0 }}>
              Target ratio: {aspectConfig.label}
            </p>
          </aside>
        </div>
        <div className="cropper-controls">
          <label htmlFor="logo-cropper-zoom">Zoom</label>
          <input
            id="logo-cropper-zoom"
            type="range"
            min={1}
            max={3}
            step={0.02}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
          />
        </div>
        <div className="button-row" style={{ justifyContent: "flex-end" }}>
          <button className="ghost" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="primary dark" type="button" onClick={handleApply} disabled={saving}>
            {saving ? "Applying..." : "Apply"}
          </button>
        </div>
      </div>
    </div>
  );
}
